// ** Next Imports
import { NavLink, useLocation, useSearchParams } from 'react-router-dom'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import { styled, useTheme } from '@mui/material/styles'
import ListItemButton from '@mui/material/ListItemButton'

// ** Configs Import
import themeConfig from 'src/configs/themeConfig'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'
import Translations from 'src/layouts/components/Translations'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'

// ** Utils
import { handleURLQueries } from 'src/@core/layouts/utils'

// ** Styled Components
const MenuNavLink = styled(ListItemButton)(({ theme, islogout, isactive }) => ({
  width: '100%',
  borderRadius: "8px",
  color: islogout === 'true' ? '#FF4444' : (isactive === 'true' ? '#FFFFFF' : '#666666'),
  transition: 'all .25s ease-in-out',
  marginBottom: '4px',
  backgroundColor: isactive === 'true' ? '#000000' : 'transparent',
  '&:hover': {
    backgroundColor: islogout === 'true' ? '#FFE6E6' : (isactive === 'true' ? '#000000' : '#F5F5F5'),
  },
  '&.active': {
    '&, &:hover': {
      backgroundColor: '#000000',
      color: '#FFFFFF',
    },
    '& .MuiTypography-root, & .MuiListItemIcon-root': {
      color: '#FFFFFF !important',
    }
  }
}))

const MenuItemTextMetaWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
})

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
  navigationBorderWidth
}) => {
  // ** Hooks
  const theme = useTheme()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // ** Vars
  const { skin, navCollapsed } = settings
  const IconTag = parent && !item.icon ? themeConfig.navSubItemIcon : item.icon
  const isLogout = item.title === 'Log Out'

  // ** Function to check if nav link is active
  const isNavLinkActive = () => {
    if (location.pathname === item.path || handleURLQueries({ ...location, query: searchParams }, item.path)) {
      return true
    } else {
      return false
    }
  }


  const conditionalBgColor = () => {
    return {
      color: isLogout ? '#FF4444' : (isNavLinkActive() ? '#FFFFFF' : '#666666'),
      '&:hover': {
        backgroundColor: isLogout ? '#FFE6E6' : (isNavLinkActive() ? '#000000' : '#F5F5F5')
      }
    }
  }

  return (
    // <CanViewNavLink navLink={item}>
    <ListItem
      disablePadding
      className='nav-link'
      disabled={item.disabled || false}
      sx={{ mt: 1.5, px: '0 !important' }}
    >
      {/* <Link to={item.path === undefined ? '/' : `${item.path}`}> */}
      <MenuNavLink
        to={item.path === undefined ? '/' : `${item.path}`}
        component={NavLink}
        className={isNavLinkActive() ? 'active' : ''}
        islogout={isLogout.toString()}
        isactive={isNavLinkActive().toString()}
        {...(item.openInNewTab ? { target: '_blank' } : null)}
        onClick={e => {
          if (item.path === undefined) {
            e.preventDefault()
            e.stopPropagation()
          }
          if (navVisible) {
            toggleNavVisibility()
          }
        }}
        sx={{
          py: 2,
          ...conditionalBgColor(),
          ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
          pl: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 24) / 8 : 4,
          pr: navCollapsed && !navHover ? ((collapsedNavWidth - navigationBorderWidth - 24) / 2 - 5) / 4 : 4
        }}
      >
        {parent ? null : (
          <ListItemIcon
            sx={{
              color: 'inherit',
              transition: 'margin .25s ease-in-out',
              ...(navCollapsed && !navHover ? { mr: 0 } : { mr: 2 }),
              ...(parent ? { ml: 1.25, mr: 3.75 } : {}) // This line should be after (navCollapsed && !navHover) condition for proper styling
            }}
          >
            <UserIcon
              icon={IconTag}
              componentType='vertical-menu'
              iconProps={{
                sx: {
                  fontSize: '1.25rem',
                  ...(!parent ? { fontSize: '1.25rem' } : {}),
                  ...(parent && item.icon ? { fontSize: '1.25rem' } : {})
                },
              }}
            />
          </ListItemIcon>
        )}

        <MenuItemTextMetaWrapper
          sx={{
            ...(isSubToSub ? { ml: 9 } : {}),
            ...(navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 })
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'inherit'
            }}
            {...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navCollapsed && !navHover)) && {
              noWrap: true
            })}
          >
            <Translations text={item.title} />
          </Typography>
          {item.badgeContent ? (
            <Chip
              label={item.badgeContent}
              color={item.badgeColor || 'primary'}
              sx={{
                ml: 1.25,
                height: 20,
                fontWeight: 500,
                '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' }
              }}
            />
          ) : null}
        </MenuItemTextMetaWrapper>
      </MenuNavLink>
      {/* </Link> */}
    </ListItem>
    // </CanViewNavLink>
  )
}

export default VerticalNavLink
